/**
 * 邮百工GUI统一渲染逻辑 - 增强型Mermaid渲染组件
 * 用于渲染高质量的Mermaid图表，包含增强功能如缩放控制、错误处理和样式主题
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { preprocessMermaidChart } from '../core/ContentUtils';
import ReactDOM from 'react-dom'; // 添加ReactDOM导入以使用Portal

// 全局存储AI修复状态的Map，key是图表内容的hash，value是修复后的代码
const aiFixedCharts = new Map();

// 生成图表内容的hash作为唯一标识
const generateChartHash = (content) => {
  if (!content) return '';
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString();
};

/**
 * 增强型Mermaid渲染组件
 * @param {Object} props - 组件属性
 * @param {string} props.chart - Mermaid图表内容
 * @param {string} props.className - 额外的CSS类名
 * @param {string} props.theme - 主题名称(default, forest, dark, neutral)
 * @param {boolean} props.zoomEnabled - 是否启用缩放功能
 * @returns {JSX.Element} - 渲染后的Mermaid组件
 */
const EnhancedMermaid = ({ 
  chart, 
  className = '', 
  theme = 'dark',
  zoomEnabled = true,
  showDebugInfo = false, // 新增调试信息开关
  onChartUpdate = null // 可选的图表更新回调
}) => {
  const containerRef = useRef(null);
  const modalContentRef = useRef(null); // 添加模态框内容引用
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖动
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }); // 拖动起始位置
  const [position, setPosition] = useState({ x: 0, y: 0 }); // 平移位置

  // 根据节点填充色自动调整文字对比度
  const applyNodeTextContrast = useCallback((svgElement) => {
    if (!svgElement) return;

    const parseColor = (value) => {
      if (!value) return null;
      const color = value.trim();
      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 3) {
          const r = parseInt(hex[0] + hex[0], 16);
          const g = parseInt(hex[1] + hex[1], 16);
          const b = parseInt(hex[2] + hex[2], 16);
          return { r, g, b };
        }
        if (hex.length === 6) {
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          return { r, g, b };
        }
      }
      const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (rgbMatch) {
        return {
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
        };
      }
      return null;
    };

    const getLuminance = ({ r, g, b }) => (0.299 * r + 0.587 * g + 0.114 * b);

    const nodes = svgElement.querySelectorAll('g.node');
    nodes.forEach((node) => {
      const shape = node.querySelector('rect, polygon, circle, path');
      if (!shape) return;
      const fill = shape.getAttribute('fill') || shape.style.fill;
      const rgb = parseColor(fill);
      if (!rgb) return;
      const luminance = getLuminance(rgb);
      const textColor = luminance > 170 ? '#111827' : '#e5e7eb';

      node.querySelectorAll('text').forEach((textEl) => {
        textEl.setAttribute('fill', textColor);
        textEl.style.fill = textColor;
      });
      node.querySelectorAll('foreignObject *').forEach((foEl) => {
        foEl.style.color = textColor;
      });
    });
  }, []);
  
  // 添加状态来存储预处理结果
  const [preprocessResult, setPreprocessResult] = useState(null);
  
  // 计算当前图表的hash
  const chartHash = generateChartHash(chart);
  
  // 检查是否有已存储的AI修复版本
  const storedFixedChart = aiFixedCharts.get(chartHash);
  
  // Mermaid修复相关状态
  const [isFixing, setIsFixing] = useState(false);
  const [fixedCode, setFixedCode] = useState(null);
  const [currentChart, setCurrentChart] = useState(storedFixedChart || chart); // 优先使用修复版本
  const [isAIFixed, setIsAIFixed] = useState(!!storedFixedChart); // 如果有存储版本则标记为AI修复
  const [fixAttemptCount, setFixAttemptCount] = useState(0); // 修复尝试次数
  const [currentFixRequestId, setCurrentFixRequestId] = useState(null); // 当前修复请求ID
  
  // 方案一：生成稳定的图表ID
  const stableChartId = useRef(null);
  const currentRenderRef = useRef(null); // 用于跟踪当前渲染
  
  // 生成基于内容的稳定ID
  const generateStableId = useCallback((content) => {
    if (!content) return 'mermaid-empty';
    
    // 简单hash算法生成内容指纹
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    
    return `mermaid-${Math.abs(hash)}-${Date.now().toString().slice(-6)}`;
  }, []);
  
  // AI修复处理函数
  const handleAIFix = () => {
    if (!preprocessResult) {
      alert("无法获取图表代码");
      return;
    }
    
    // 增加修复尝试次数
    const nextAttemptCount = fixAttemptCount + 1;
    setFixAttemptCount(nextAttemptCount);
    
    // 确定要发送给AI的代码：如果当前已经是AI修复的代码，则使用当前代码；否则使用原始代码
    const codeToFix = isAIFixed ? currentChart : preprocessResult.originalContent;
    const currentError = error || "图表渲染失败";
    
    console.log(`🤖 第${nextAttemptCount}次AI修复请求`);
    console.log("是否为迭代修复:", isAIFixed);
    console.log("代码来源:", isAIFixed ? "上次AI修复结果" : "原始代码");
    console.log("发送给AI的代码长度:", codeToFix.length);
    console.log("发送给AI的代码:", codeToFix);
    console.log("当前错误信息:", currentError);
    
    // 开始新的AI修复时，重置之前的AI修复状态（准备接收新的修复结果）
    setIsAIFixed(false);
    setIsFixing(true);
    
    // 生成唯一的请求ID
    const requestId = `fix_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    setCurrentFixRequestId(requestId);
    
    // 调用全局函数发送修复请求
    if (window.requestMermaidFix) {
      window.requestMermaidFix(codeToFix, currentError, requestId);
    } else {
      console.error("修复服务不可用");
      setIsFixing(false);
      setCurrentFixRequestId(null);
      alert("修复服务暂时不可用，请稍后再试");
    }
  };

  // 安全的剪贴板写入函数
  const safeWriteToClipboard = async (text) => {
    try {
      // 检查是否支持现代剪贴板API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      
      // 降级方案：使用传统的document.execCommand
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    } catch (err) {
      console.warn('剪贴板操作失败:', err);
      return false;
    }
  };

  // 应用修复后的代码
  const applyFixedCode = async (newCode) => {
    console.log("应用修复后的代码:", newCode);
    
    // 尝试复制到剪贴板作为备用（非阻塞）
    try {
      await safeWriteToClipboard(newCode);
      console.log("代码已复制到剪贴板");
    } catch (err) {
      console.warn("复制到剪贴板失败，但不影响功能:", err);
    }
    
    // 将修复后的代码存储到全局Map中，以原始图表hash为key
    aiFixedCharts.set(chartHash, newCode);
    console.log(`AI修复代码已存储，hash: ${chartHash}`);
    
    // 标记为AI修复的代码，跳过自动处理
    setIsAIFixed(true);
    
    // 更新当前图表内容
    setCurrentChart(newCode);
    
    // 清除错误状态，准备重新渲染
    setError(null);
    setFixedCode(null); // 隐藏修复结果显示
    
    // 如果有回调函数，也通知父组件
    if (onChartUpdate) {
      onChartUpdate(newCode);
    }
    
    console.log("图表代码已自动应用，正在重新渲染...");
  };

  // 监听修复结果事件
  useEffect(() => {
    //console.log(`🔧 设置事件监听器，当前期待的requestId: ${currentFixRequestId}`);
    
    const handleFixSuccess = (event) => {
      const { fixedCode, originalCode, message, requestId } = event.detail;
      console.log(`📨 收到修复成功事件:`, { requestId, message, currentFixRequestId });
      
      // 使用请求ID进行精确匹配，避免并发冲突
      if (requestId === currentFixRequestId) {
        console.log(`🎉 修复成功匹配 (ID: ${requestId}):`, message);
        setIsFixing(false);
        setFixedCode(fixedCode);
        setCurrentFixRequestId(null); // 清除请求ID
        
        // 显示成功提示
        alert(`修复成功！请查看下方的修复结果，可以点击"自动应用"按钮来应用修复后的代码。`);
      } else {
        console.log(`⚠️ 修复成功ID不匹配 - 收到: ${requestId}, 期待: ${currentFixRequestId}`);
      }
    };
    
    const handleFixError = (event) => {
      const { message, requestId } = event.detail;
      console.log(`📨 收到修复失败事件:`, { requestId, message, currentFixRequestId });
      
      // 使用请求ID进行精确匹配
      if (requestId === currentFixRequestId) {
        console.error(`❌ 修复失败匹配 (ID: ${requestId}):`, message);
        setIsFixing(false);
        setCurrentFixRequestId(null); // 清除请求ID
        alert(`修复失败: ${message}`);
      } else {
        console.log(`⚠️ 修复失败ID不匹配 - 收到: ${requestId}, 期待: ${currentFixRequestId}`);
      }
    };
    
    window.addEventListener('mermaidFixSuccess', handleFixSuccess);
    window.addEventListener('mermaidFixError', handleFixError);
    
    return () => {
      window.removeEventListener('mermaidFixSuccess', handleFixSuccess);
      window.removeEventListener('mermaidFixError', handleFixError);
    };
  }, [currentFixRequestId]); // 修改依赖项为currentFixRequestId

  // 监听外部chart prop的变化，更新内部状态
  useEffect(() => {
    // 检查新的图表是否有已存储的修复版本
    const newChartHash = generateChartHash(chart);
    const newStoredFixedChart = aiFixedCharts.get(newChartHash);
    
    if (newStoredFixedChart) {
      // 如果有存储的修复版本，使用修复版本
      setCurrentChart(newStoredFixedChart);
      setIsAIFixed(true);
      // 注意：不重置修复次数，因为这是已存储的修复版本
    } else {
      // 如果没有修复版本，使用原始图表并重置修复次数
      setCurrentChart(chart);
      setIsAIFixed(false);
      setFixAttemptCount(0); // 新图表重置修复次数
    }
  }, [chart]);

  // 处理图表渲染
  useEffect(() => {
    if (!currentChart) {
      setLoading(false);
      return;
    }
    
    // 方案二：生成渲染令牌防止竞态条件
    const renderToken = Math.random().toString(36).substring(2, 9);
    currentRenderRef.current = renderToken;
    
    // 标记组件为加载中状态
    setLoading(true);
    setError(null);
    
    // 处理图表内容 - 使用新的预处理函数
    const result = preprocessMermaidChart(currentChart, isAIFixed);
    setPreprocessResult(result);
    
    // 如果预处理发现严重错误，直接显示错误
    if (!result.validation.isValid && result.validation.errors.length > 0) {
      // 检查是否为当前渲染
      if (currentRenderRef.current !== renderToken) return;
      
      setError(`语法错误: ${result.validation.errors.join(', ')}`);
      setLoading(false);
      return;
    }
    
    const processedChart = result.content;
    
    // 生成或复用稳定的图表ID
    if (!stableChartId.current || stableChartId.current.includes('empty')) {
      stableChartId.current = generateStableId(processedChart);
    }
    
    const isDarkTheme = theme === 'dark';

    // 异步加载mermaid库 - 减小初始加载体积
    import('mermaid').then(mermaid => {
      try {
        const darkThemeVariables = {
          background: '#1e1e1e',
          primaryColor: '#2b2b2b',
          secondaryColor: '#2a2f3a',
          tertiaryColor: '#3a2f2f',
          primaryTextColor: '#e5e7eb',
          secondaryTextColor: '#cbd5f5',
          tertiaryTextColor: '#a1a1aa',
          primaryBorderColor: '#3a3a3a',
          secondaryBorderColor: '#3f3f46',
          tertiaryBorderColor: '#4b5563',
          lineColor: '#9ca3af',
          textColor: '#e5e7eb',
          nodeBorder: '#4b5563',
          nodeBkg: '#2b2f36',
          clusterBkg: '#1f2937',
          clusterBorder: '#374151',
          defaultLinkColor: '#9ca3af',
          titleColor: '#e5e7eb',
          edgeLabelBackground: '#111827',
          actorBorder: '#4b5563',
          actorBkg: '#1f2937',
          actorTextColor: '#e5e7eb',
          signalColor: '#e5e7eb',
          signalTextColor: '#e5e7eb',
          labelBoxBkgColor: '#111827',
          labelBoxBorderColor: '#4b5563',
          labelTextColor: '#e5e7eb',
          loopTextColor: '#e5e7eb',
          noteBkgColor: '#3b2f2f',
          noteTextColor: '#f3f4f6',
          noteBorderColor: '#6b7280',
          altBackground: '#1f2937',
          fontSize: '15px',
        };

        const lightThemeVariables = {
          // --- 浅色主题配置 (Light Mode) ---
          // 使用清新的浅色背景，确保文字清晰可读
          background: '#ffffff', // 纯白背景
          primaryColor: '#f0f7ff', // 主节点背景 - 极浅蓝
          secondaryColor: '#f5f5f5', // 次要背景 - 浅灰
          tertiaryColor: '#fff9e6', // 第三背景 - 浅黄
          primaryTextColor: '#333333', // 主要文字 - 深灰 (高对比度)
          secondaryTextColor: '#666666', // 次要文字
          tertiaryTextColor: '#888888', // 第三文字
          primaryBorderColor: '#d1e4f9', // 主节点边框 - 柔和蓝
          secondaryBorderColor: '#e0e0e0', // 次要边框
          tertiaryBorderColor: '#ffe082', // 第三边框
          lineColor: '#999999', // 连线颜色 - 中灰
          textColor: '#333333', // 全局默认文字 - 深灰
          // --- 特定元素配色 ---
          nodeBorder: '#cce0ff', // 节点边框
          nodeBkg: '#f0f8ff', // 节点背景 - 爱丽丝蓝
          clusterBkg: '#fafafa', // 子图背景 - 极浅灰
          clusterBorder: '#e0e0e0', // 子图边框
          defaultLinkColor: '#999999', // 连线颜色
          titleColor: '#333333', // 标题颜色
          edgeLabelBackground: '#ffffff', // 连线标签背景 - 纯白
          actorBorder: '#d1e4f9',
          actorBkg: '#f0f7ff',
          actorTextColor: '#333333',
          // --- 甘特图/序列图等 ---
          signalColor: '#333333',
          signalTextColor: '#333333',
          labelBoxBkgColor: '#ffffff',
          labelBoxBorderColor: '#d1e4f9',
          labelTextColor: '#333333',
          loopTextColor: '#333333',
          noteBkgColor: '#fff9e6', // 笔记背景 - 浅黄
          noteTextColor: '#333333',
          noteBorderColor: '#ffe082',
          // --- 状态图 ---
          altBackground: '#fafafa',
          fontSize: '15px',
        };

        // 初始化mermaid（使用 base 主题 + 变量，避免主题覆盖导致不生效）
        mermaid.default.initialize({
          startOnLoad: false,
          securityLevel: 'loose',
          suppressErrorRendering: true, // 禁用Mermaid自带的炸弹错误显示
          theme: 'base',
          flowchart: {
            htmlLabels: true,
            curve: 'basis'
          },
          sequence: {
            diagramMarginX: 50,
            diagramMarginY: 10,
            actorMargin: 80,
            boxMargin: 10,
            boxTextMargin: 5,
            noteMargin: 10
          },
          er: {
            layoutDirection: 'TB',
            minEntityWidth: 100,
            minEntityHeight: 75,
            entityPadding: 15
          },
          gantt: {
            titleTopMargin: 25,
            barHeight: 20,
            barGap: 4,
            topPadding: 50,
            leftPadding: 75,
            gridLineStartPadding: 35
          },
          themeVariables: isDarkTheme ? darkThemeVariables : lightThemeVariables
        });
        
        // 使用稳定ID渲染图表
        mermaid.default.render(stableChartId.current, processedChart).then(result => {
          // 检查是否为当前渲染（防止竞态条件）
          if (currentRenderRef.current !== renderToken) {
            ///console.log('渲染已过期，忽略结果:', renderToken);
            return;
          }
          
          // 设置渲染后的SVG内容
          const svgElement = new DOMParser().parseFromString(result.svg, 'image/svg+xml').querySelector('svg');

          if (svgElement) {
            // 移除固定的尺寸属性，以便通过CSS控制
            svgElement.removeAttribute('width');
            svgElement.removeAttribute('height');

            // 设置样式以确保SVG能自适应容器并完整显示
            svgElement.style.width = '100%';
            svgElement.style.height = '100%';
            svgElement.style.objectFit = 'contain';

            // 根据节点底色自动调整文字对比度
            applyNodeTextContrast(svgElement);

            // 方案六：安全的SVG内容更新
            setSvgContent(svgElement.outerHTML);
          } else {
            setSvgContent(result.svg);
          }

          setLoading(false);
        }).catch(err => {
          // 检查是否为当前渲染
          if (currentRenderRef.current !== renderToken) return;
          
      //    console.error('Mermaid渲染错误:', err);
          setError(`图表渲染失败: ${err.message || '未知错误'}`);
          setLoading(false);
        });
      } catch (err) {
        // 检查是否为当前渲染
        if (currentRenderRef.current !== renderToken) return;
        
        console.error('Mermaid处理错误:', err);
        setError(`图表处理失败: ${err.message || '未知错误'}`);
        setLoading(false);
      }
    }).catch(err => {
      // 检查是否为当前渲染
      if (currentRenderRef.current !== renderToken) return;
      
      console.error('Mermaid库加载失败:', err);
      setError('图表库加载失败，请刷新页面重试');
      setLoading(false);
    });
    
    // 方案四：修复的组件清理机制
    return () => {
      // 取消当前渲染
      if (currentRenderRef.current === renderToken) {
        currentRenderRef.current = null;
      }
      
      // 不直接操作DOM，让React处理
      // 只清理JavaScript状态
      setSvgContent('');
      stableChartId.current = null;
    };
  }, [currentChart, theme, isAIFixed]);
  
  // 方案六：安全处理SVG内容变化
  useEffect(() => {
    // 当SVG内容变化时，确保容器正确更新
    if (containerRef.current && svgContent) {
      // React会通过dangerouslySetInnerHTML自动处理内容更新
      // 这里只做一些必要的样式确保
      const svgElement = containerRef.current.querySelector('svg');
      if (svgElement) {
        svgElement.style.width = '100%';
        svgElement.style.height = '100%';
        svgElement.style.objectFit = 'contain';
      }
    }
  }, [svgContent]);
  
  // 处理缩放控制
  const handleZoomIn = () => {
    if (zoomLevel < 200) {
      setZoomLevel(prevZoom => prevZoom + 10);
    }
  };
  
  const handleZoomOut = () => {
    if (zoomLevel > 50) {
      setZoomLevel(prevZoom => prevZoom - 10);
    }
  };
  
  const handleZoomReset = () => {
    setZoomLevel(100);
    setPosition({ x: 0, y: 0 }); // 同时重置平移位置
  };

  // 处理双击事件
  const handleDoubleClick = () => {
    setIsModalOpen(true);
    // 重置平移和缩放状态
    setPosition({ x: 0, y: 0 });
    setZoomLevel(100);
  };

  // 关闭模态框
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 鼠标拖动平移相关函数
  const handleMouseDown = (e) => {
    // 仅当缩放级别大于100%时才启用拖动功能
    if (zoomLevel <= 100) return;
    
    // 确保只有左键点击才开始拖动
    if (e.button !== 0) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setPosition({ x: newX, y: newY });
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // 添加鼠标滚轮缩放功能
  const handleWheel = (e) => {
    e.preventDefault();
    
    // 根据滚轮方向调整缩放级别
    if (e.deltaY < 0) { // 滚轮向上
      if (zoomLevel < 200) {
        setZoomLevel(prev => Math.min(prev + 5, 200));
      }
    } else { // 滚轮向下
      if (zoomLevel > 50) {
        setZoomLevel(prev => Math.max(prev - 5, 50));
      }
    }
  };
  
  // 触摸设备支持
  const handleTouchStart = (e) => {
    // 仅当缩放级别大于100%时才启用拖动功能
    if (zoomLevel <= 100) return;
    
    // 确保只有单指触摸才开始拖动
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    setPosition({ x: newX, y: newY });
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
  };
  
  // 添加触摸设备的缩放手势支持
  const handlePinchZoom = () => {
    // 手势缩放功能可以在这里实现
    // 由于需要复杂的手势识别，这部分可以在将来扩展
  };

  // 为拖动效果添加全局事件监听器
  useEffect(() => {
    if (isModalOpen && isDragging) {
      // 全局监听鼠标移动和松开事件
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // 触摸设备事件
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isModalOpen, isDragging]);
  
  // 模态框组件 - 使用Portal实现
  const FullscreenModal = () => {
    return ReactDOM.createPortal(
      <div className="mermaid-modal-overlay">
        <div className="mermaid-modal-content" ref={modalContentRef}>
          <button className="mermaid-modal-close" onClick={closeModal}>×</button>
          <div className="modal-zoom-controls">
            <button onClick={handleZoomOut} disabled={zoomLevel <= 50}>−</button>
            <span>{zoomLevel}%</span>
            <button onClick={handleZoomIn} disabled={zoomLevel >= 200}>+</button>
            <button onClick={handleZoomReset}>重置</button>
          </div>
          <div 
            className={`mermaid-modal-svg-wrapper ${isDragging ? 'dragging' : ''}`}
            style={{ 
              transform: `scale(${zoomLevel / 100}) translate(${position.x / (zoomLevel / 100)}px, ${position.y / (zoomLevel / 100)}px)` 
            }}
            onMouseDown={handleMouseDown}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%', 
              height: '100%' 
            }} dangerouslySetInnerHTML={{ __html: svgContent }} />
          </div>
          {/* 添加平移和缩放操作提示 */}
          <div className="modal-instructions">
            {zoomLevel > 100 ? "拖动可平移图表 · 滚轮可缩放" : "使用滚轮可缩放图表"}
          </div>
        </div>
      </div>,
      document.body // 确保模态框直接挂载在document.body
    );
  };
  
  // 主要渲染
  const isDarkTheme = theme === 'dark';

  return (
    <div 
      className={`enhanced-mermaid-container ${className} ${error ? 'has-error' : ''}`} 
      ref={containerRef}
      data-mermaid-source={chart || ''}
    >
      {/* 加载状态 */}
      {loading && (
        <div className="mermaid-loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">图表渲染中...</div>
        </div>
      )}
      
      {/* 增强的错误显示 */}
      {error && (
        <div className="mermaid-error">
          <div className="error-header">
            <div className="error-title">图表渲染失败</div>
            <details className="error-details">
              <summary className="error-details-summary">点击查看详细信息</summary>
              <div className="error-details-content">
                <div className="error-message">
                  <strong>错误信息:</strong> {error}
                </div>
                
                {/* 显示验证信息 */}
                {preprocessResult && preprocessResult.validation && (
                  <div className="validation-info">
                    {preprocessResult.validation.warnings.length > 0 && (
                      <div className="warnings">
                        <div className="warnings-title">⚠️ 警告:</div>
                        <ul>
                          {preprocessResult.validation.warnings.map((warning, idx) => (
                            <li key={idx}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {preprocessResult.validation.suggestions.length > 0 && (
                      <div className="suggestions">
                        <div className="suggestions-title">💡 建议:</div>
                        <ul>
                          {preprocessResult.validation.suggestions.map((suggestion, idx) => (
                            <li key={idx}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* 显示原始代码 */}
                {preprocessResult && preprocessResult.originalContent && (
                  <details className="source-code-details">
                    <summary>查看原始代码</summary>
                    <div className="source-code">
                      <div className="code-header">
                        <span>{isAIFixed ? `AI修复版本${fixAttemptCount}代码:` : '原始Mermaid代码:'}</span>
                        <button 
                          onClick={async () => {
                            try {
                              await safeWriteToClipboard(preprocessResult.originalContent);
                            } catch (err) {
                              console.warn("复制失败:", err);
                            }
                          }}
                          className="copy-code-button"
                          title="复制代码"
                        >
                          📋
                        </button>
                      </div>
                      <pre>{preprocessResult.originalContent}</pre>
                    </div>
                    
                    {!isAIFixed && preprocessResult.content !== preprocessResult.originalContent && (
                      <div className="processed-code">
                        <div className="code-header">
                          <span>处理后代码:</span>
                          <button 
                            onClick={async () => {
                              try {
                                await safeWriteToClipboard(preprocessResult.content);
                              } catch (err) {
                                console.warn("复制失败:", err);
                              }
                            }}
                            className="copy-code-button"
                            title="复制处理后的代码"
                          >
                            📋
                          </button>
                        </div>
                        <pre>{preprocessResult.content}</pre>
                      </div>
                    )}
                    
                    {isAIFixed && (
                      <div className="ai-status-info">
                        <div className="ai-status-text">
                          💡 AI修复版本已跳过自动预处理，代码保持AI修复的原样
                        </div>
                      </div>
                    )}
                  </details>
                )}

                {/* 显示修复后的代码 */}
                {fixedCode && (
                  <div className="fixed-code-section">
                    <div className="fixed-code-header">
                      <span>🎉 AI修复后的代码:</span>
                      <div className="fixed-code-actions">
                        <button 
                          onClick={async () => {
                            try {
                              await safeWriteToClipboard(fixedCode);
                            } catch (err) {
                              console.warn("复制失败:", err);
                            }
                          }}
                          className="copy-code-button"
                          title="复制修复后的代码"
                        >
                          📋 复制
                        </button>
                        <button 
                          onClick={() => applyFixedCode(fixedCode)}
                          className="apply-fix-button"
                          title="自动应用修复后的代码并重新渲染图表"
                        >
                          ✅ 自动应用
                        </button>
                      </div>
                    </div>
                    <pre className="fixed-code-content">{fixedCode}</pre>
                  </div>
                )}
                
                {/* AI修复按钮 */}
                <div className="error-actions">
                  <button 
                    className="ai-fix-button"
                    onClick={handleAIFix}
                    disabled={isFixing}
                    title="使用AI自动修复图表语法错误"
                  >
                    {isFixing ? (
                      <>
                        <span className="loading-spinner-small"></span>
                        第{fixAttemptCount + 1}次修复中...
                      </>
                    ) : (
                      <>
                        🤖 AI修复图表{fixAttemptCount > 0 ? ` (第${fixAttemptCount + 1}次)` : ''}
                      </>
                    )}
                  </button>
                </div>

                <div className="error-help">
                  <div className="help-title">需要帮助?</div>
                  <div className="help-links">
                    <a href="https://mermaid.js.org/syntax/flowchart.html" target="_blank" rel="noopener noreferrer">
                      Mermaid语法文档
                    </a>
                    <span> | </span>
                    <a href="https://mermaid.live/" target="_blank" rel="noopener noreferrer">
                      在线编辑器
                    </a>
                  </div>
                </div>
              </div>
            </details>
          </div>
        </div>
      )}
      
                {/* 图表显示 */}
      {!loading && !error && svgContent && (
        <div className="mermaid-content">
          {/* 调试信息显示 */}
          {showDebugInfo && preprocessResult && (
            <div className="debug-info">
              <details className="debug-details">
                <summary>🔍 处理信息</summary>
                <div className="debug-content">
                  {preprocessResult.validation.warnings.length > 0 && (
                    <div className="debug-warnings">
                      <strong>自动修复的问题:</strong>
                      <ul>
                        {preprocessResult.validation.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preprocessResult.content !== preprocessResult.originalContent && (
                    <div className="debug-changes">
                      <strong>✅ 已应用优化:</strong>
                      <ul>
                        <li>修复Unicode字符</li>
                        <li>规范化空格和符号</li>
                        <li>添加中文标签引号</li>
                      </ul>
                    </div>
                  )}
                  
                  {preprocessResult.validation.suggestions.length > 0 && (
                    <div className="debug-suggestions">
                      <strong>状态:</strong>
                      <ul>
                        {preprocessResult.validation.suggestions.map((suggestion, idx) => (
                          <li key={idx}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            </div>
          )}
          
          {/* 外部缩放控制 */}
          {zoomEnabled && (
            <div className="zoom-controls">
              <button className="fullscreen-button" onClick={handleDoubleClick} title="全屏查看">⤢</button>
            </div>
          )}
          
          {/* 方案六：React控制的SVG容器DOM管理 */}
          <div 
            ref={containerRef}
            className="mermaid-svg-wrapper"
            style={{ transform: `scale(${zoomLevel / 100})` }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
            onDoubleClick={handleDoubleClick}
            key={`svg-container-${stableChartId.current || 'default'}`} // 确保容器与图表ID关联
          />
        </div>
      )}

      {/* 条件渲染模态框 */}
      {isModalOpen && <FullscreenModal />}
      
      {/* 样式 */}
      <style jsx>{`
        .enhanced-mermaid-container {
          width: 100%;
          position: relative;
          min-height: 50px;
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow: hidden;
        }

        /*
         * Mermaid 画布背景统一：
         * - 默认固定为“气泡未选中”底色（更和谐、更稳定）
         * - 通过 CSS 变量保留可覆盖能力（如全屏/特定容器）
         * - 仅做样式层改动，不触碰渲染逻辑
         */
        .enhanced-mermaid-container {
          --ybg-mermaid-surface-bg: ${isDarkTheme ? '#252526' : '#ffffff'};
          --ybg-mermaid-surface-border: ${isDarkTheme ? 'rgba(255, 255, 255, 0.10)' : '#e0e0e0'};
        }
        
        .mermaid-loading {
          width: 100%;
          min-height: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          color: #666;
        }
        
        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          border-top-color: #3498db;
          animation: spin 1s ease-in-out infinite;
          margin-bottom: 10px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .mermaid-error {
          width: 100%;
          padding: 10px 12px;
          background-color: #2c3e50;
          border: 1px solid #34495e;
          border-radius: 4px;
          color: #bdc3c7;
          margin: 8px 0;
          display: flex;
          flex-direction: column;
          text-align: left;
          font-size: 13px;
        }
        
        .error-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 6px;
        }
        
        .error-title {
          font-weight: 500;
          font-size: 14px;
          color: #bdc3c7;
        }
        

        
        .error-details {
          flex: 1;
        }
        
        .error-details-summary {
          cursor: pointer;
          font-size: 12px;
          color: #bdc3c7;
          padding: 4px 8px;
          background-color: #34495e;
          border-radius: 3px;
          border: none;
          outline: none;
          user-select: none;
          transition: background-color 0.2s ease;
        }
        
        .error-details-summary:hover {
          background-color: #3d566e;
        }
        
        .error-details-content {
          padding: 8px 0;
          border-top: 1px solid #7f8c8d;
          margin-top: 4px;
        }
        
        .error-message {
          color: #bdc3c7;
          margin-bottom: 12px;
          line-height: 1.4;
          font-size: 12px;
        }
        
        .validation-info {
          margin-bottom: 12px;
        }
        
        .warnings, .suggestions {
          margin-bottom: 8px;
        }
        
        .warnings-title, .suggestions-title {
          font-weight: 500;
          margin-bottom: 4px;
          color: #bdc3c7;
          font-size: 12px;
        }
        
        .suggestions-title {
          color: #bdc3c7;
        }
        
        .warnings ul, .suggestions ul {
          margin: 0;
          padding-left: 16px;
          color: #95a5a6;
          font-size: 11px;
        }
        
        .warnings li, .suggestions li {
          margin-bottom: 2px;
          line-height: 1.3;
        }
        
        .source-code-details {
          border: 1px solid #7f8c8d;
          border-radius: 3px;
          margin-bottom: 12px;
        }
        
        .source-code-details summary {
          padding: 6px 8px;
          background-color: #34495e;
          cursor: pointer;
          font-weight: 500;
          border-radius: 3px 3px 0 0;
          font-size: 12px;
          color: #bdc3c7;
        }
        
        .source-code-details summary:hover {
          background-color: #3d566e;
        }
        
        .source-code, .processed-code {
          margin-bottom: 10px;
        }
        
        .code-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 8px;
          background-color: #34495e;
          border-bottom: 1px solid #7f8c8d;
          font-weight: 500;
          color: #bdc3c7;
          font-size: 11px;
        }
        
        .copy-code-button {
          background: none;
          border: 1px solid #ccc;
          border-radius: 3px;
          padding: 2px 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }
        
        .copy-code-button:hover {
          background-color: #f0f0f0;
          border-color: #999;
        }
        
        .source-code pre, .processed-code pre {
          margin: 0;
          padding: 12px;
          background-color: #fafafa;
          border: none;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          color: #333;
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        
        .processed-code .code-header {
          background-color: #e8f5e8;
          color: #2e7d32;
        }
        
        .processed-code pre {
          background-color: #f1f8e9;
        }
        
        .error-help {
          padding-top: 8px;
          border-top: 1px solid #7f8c8d;
        }
        
        .help-title {
          font-weight: 500;
          margin-bottom: 4px;
          color: #bdc3c7;
          font-size: 11px;
        }
        
        .help-links {
          color: #95a5a6;
          font-size: 11px;
        }
        
        .help-links a {
          color: #95a5a6;
          text-decoration: none;
        }
        
        .help-links a:hover {
          text-decoration: underline;
          color: #bdc3c7;
        }
        
        /* 调试信息样式 */
        .debug-info {
          margin-bottom: 10px;
        }
        
        .debug-details {
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          background-color: #f9f9f9;
        }
        
        .debug-details summary {
          padding: 6px 10px;
          background-color: #f0f0f0;
          cursor: pointer;
          font-size: 12px;
          color: #666;
          border-radius: 4px 4px 0 0;
          user-select: none;
        }
        
        .debug-details summary:hover {
          background-color: #e8e8e8;
        }
        
        .debug-content {
          padding: 10px;
          font-size: 11px;
          color: #555;
        }
        
        .debug-warnings, .debug-changes, .debug-suggestions {
          margin-bottom: 8px;
        }
        
        .debug-warnings ul, .debug-changes ul, .debug-suggestions ul {
          margin: 4px 0 0 0;
          padding-left: 16px;
        }
        
        .debug-warnings li, .debug-changes li, .debug-suggestions li {
          margin-bottom: 2px;
          line-height: 1.3;
        }
        
        .debug-warnings strong {
          color: #f57c00;
        }
        
        .debug-changes strong {
          color: #388e3c;
        }
        
        .debug-suggestions strong {
          color: #1976d2;
        }
        
        .enhanced-mermaid-container .mermaid-content {
          width: 100%;
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          background-color: var(--ybg-mermaid-surface-bg);
          padding: 15px;
          border-radius: 8px;
          overflow: auto;
          box-shadow: ${isDarkTheme ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'};
          border: 1px solid var(--ybg-mermaid-surface-border);
        }
        
        .enhanced-mermaid-container .mermaid-svg-wrapper {
          width: 100%;
          display: flex;
          justify-content: center;
          overflow: auto;
          transition: transform 0.2s ease;
          transform-origin: center top;
        }
        
        /* 确保SVG背景与“固定画布底色”一致 */
        .enhanced-mermaid-container .mermaid-svg-wrapper :global(svg) {
          background-color: var(--ybg-mermaid-surface-bg) !important;
          display: block; 
        }

        /* 强制覆盖 Mermaid 生成的背景矩形 */
        .enhanced-mermaid-container .mermaid-svg-wrapper :global(svg) :global(.background),
        .enhanced-mermaid-container .mermaid-svg-wrapper :global(svg) :global(rect.background) {
          fill: var(--ybg-mermaid-surface-bg) !important;
        }
        
        .zoom-controls {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 3px 5px; /* 稍微调整内边距 */
          margin-bottom: 5px;
          background-color: ${isDarkTheme ? 'rgba(31, 41, 55, 0.85)' : 'rgba(255, 255, 255, 0.8)'}; /* 半透明背景 */
          border: 1px solid ${isDarkTheme ? '#374151' : '#dee2e6'}; /* 边框 */
          border-radius: 4px;
          box-shadow: ${isDarkTheme ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.1)'};
          opacity: 0.8; /* 初始透明度 */
          user-select: none;
          position: absolute;
          top: 2px;
          right: 2px;
          z-index: 10;
          transition: opacity 0.2s ease;
        }
        
        .enhanced-mermaid-container:hover .zoom-controls {
          opacity: 1;
        }
        
        .zoom-button,
        .zoom-reset {
          background-color: ${isDarkTheme ? '#111827' : '#f1f3f5'};
          border: 1px solid ${isDarkTheme ? '#374151' : '#ced4da'};
          color: ${isDarkTheme ? '#e5e7eb' : '#495057'};
          width: 22px; /* 稍微增大按钮 */
          height: 22px;
          font-size: 15px;
          display: flex; /* 确保图标居中 */
          align-items: center;
          justify-content: center;
          margin: 0 2px;
          padding: 0;
          line-height: 1;
        }
        
        .zoom-reset {
          font-size: 12px; /* 重置按钮字体稍小 */
          padding: 2px 6px; /* 调整内边距 */
        }
        
        .zoom-button:hover,
        .zoom-reset:hover {
          background-color: ${isDarkTheme ? '#1f2937' : '#e9ecef'};
          border-color: ${isDarkTheme ? '#4b5563' : '#adb5bd'};
          color: ${isDarkTheme ? '#ffffff' : '#212529'};
        }
        
        .zoom-button:disabled {
          background-color: ${isDarkTheme ? '#0f172a' : '#f8f9fa'};
          border-color: ${isDarkTheme ? '#1f2937' : '#e9ecef'};
          color: ${isDarkTheme ? '#6b7280' : '#adb5bd'};
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .zoom-level {
          margin: 0 5px; /* 稍微增大间距 */
          font-size: 12px; /* 字体稍大 */
          min-width: 40px; /* 最小宽度 */
          text-align: center;
          background-color: ${isDarkTheme ? '#111827' : '#ffffff'};
          padding: 3px 5px; /* 调整内边距 */
          border: 1px solid ${isDarkTheme ? '#374151' : '#ced4da'};
          border-radius: 3px;
          color: ${isDarkTheme ? '#e5e7eb' : '#495057'};
        }
        
        .fullscreen-button {
          background-color: ${isDarkTheme ? '#111827' : '#f1f3f5'};
          border: 1px solid ${isDarkTheme ? '#374151' : '#ced4da'};
          color: ${isDarkTheme ? '#e5e7eb' : '#495057'};
          width: 22px;
          height: 22px;
          font-size: 15px;
          display: flex; /* 确保图标居中 */
          align-items: center;
          justify-content: center;
          margin-left: 5px;
          padding: 0;
          line-height: 1;
        }
        
        .fullscreen-button:hover {
          background-color: ${isDarkTheme ? '#1f2937' : '#e9ecef'};
          border-color: ${isDarkTheme ? '#4b5563' : '#adb5bd'};
          color: ${isDarkTheme ? '#93c5fd' : '#007bff'};
          transform: scale(1.05);
        }

        /* <--- 新增：模态框样式 ---> */
        .mermaid-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999999 !important; /* <--- 进一步提高z-index值，与文件面板一致 */
          pointer-events: auto; /* 确保可以接收鼠标事件 */
          width: 100vw !important; /* 强制宽度占满视口 */
          height: 100vh !important; /* 强制高度占满视口 */
          margin: 0 !important; /* 移除可能的外边距 */
          padding: 0 !important; /* 移除可能的内边距 */
        }
        
        .mermaid-modal-content {
          background-color: ${isDarkTheme ? '#111827' : '#ffffff'};
          border: 1px solid ${isDarkTheme ? '#374151' : '#dee2e6'};
          box-shadow: ${isDarkTheme ? 'none' : '0 4px 15px rgba(0, 0, 0, 0.15)'};
          color: ${isDarkTheme ? '#e5e7eb' : '#212529'};
          position: relative;
          width: 90vw !important; /* 调整为90%视口宽度 */
          height: 90vh !important; /* 调整为90%视口高度 */
          max-width: 1400px; /* 设置最大宽度 */
          display: flex;
          flex-direction: column;
          overflow: hidden; /* 防止内容溢出 */
          transform: none !important; /* 防止transform属性创建新的堆叠上下文 */
          z-index: 99999;
        }
        
        .mermaid-modal-close {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 28px;
          font-weight: bold;
          color: ${isDarkTheme ? '#e5e7eb' : '#6c757d'};
          cursor: pointer;
          line-height: 1;
          z-index: 1000000 !important; /* 确保关闭按钮始终可见 */
          background-color: ${isDarkTheme ? 'rgba(17, 24, 39, 0.9)' : 'rgba(233, 236, 239, 0.8)'};
          border: 1px solid ${isDarkTheme ? '#374151' : '#ced4da'};
          border-radius: 50%; /* 圆形按钮 */
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .mermaid-modal-close:hover {
          background-color: #dc3545;
          color: #ffffff;
          border-color: #dc3545;
          transform: scale(1.1);
        }

        .modal-zoom-controls {
          display: flex;
          align-items: center;
          justify-content: center; /* 居中显示 */
          padding: 8px 0;
          margin-bottom: 10px;
          border-bottom: 1px solid ${isDarkTheme ? '#374151' : '#dee2e6'};
          z-index: 10; /* 确保控制器可见 */
        }
        
        .modal-zoom-controls button {
          background-color: ${isDarkTheme ? '#0f172a' : '#f8f9fa'};
          border: 1px solid ${isDarkTheme ? '#374151' : '#ced4da'};
          color: ${isDarkTheme ? '#e5e7eb' : '#495057'};
          padding: 5px 10px;
          margin: 0 5px;
          cursor: pointer;
        }
        
        .modal-zoom-controls button:hover {
          background-color: ${isDarkTheme ? '#1f2937' : '#e9ecef'};
          border-color: ${isDarkTheme ? '#4b5563' : '#adb5bd'};
        }
        
        .modal-zoom-controls span {
          margin: 0 10px;
          font-weight: bold;
          color: ${isDarkTheme ? '#e5e7eb' : '#495057'};
        }
        
        .mermaid-modal-svg-wrapper {
          flex-grow: 1; /* 占据剩余空间 */
          overflow: auto; /* 允许内容滚动 */
          display: flex; /* 使用 flex 居中 */
          align-items: center;
          justify-content: center;
          transform-origin: center center; /* 缩放中心 */
          background-color: ${isDarkTheme ? '#0f172a' : '#ffffff'};
          border-radius: 5px;
          padding: 10px;
          cursor: ${zoomLevel > 100 ? 'grab' : 'default'}; /* 根据缩放级别显示抓手光标 */
          position: relative; /* 为了更好的定位控制 */
        }

        /* 全屏模态框内：让 SVG 画布底色与模态背景一致（不影响气泡内的固定底色） */
        .mermaid-modal-svg-wrapper {
          --ybg-mermaid-surface-bg: ${isDarkTheme ? '#0f172a' : '#ffffff'};
          --ybg-mermaid-surface-border: ${isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : '#e0e0e0'};
        }
        
        /* 拖动时的光标样式 */
        .mermaid-modal-svg-wrapper.dragging {
          cursor: grabbing;
        }
        
        /* 确保模态框内的SVG也能正确显示和居中 */
        .mermaid-modal-svg-wrapper :global(svg) {
           max-width: none; /* 允许SVG在缩放时超出容器 */
           max-height: none; /* 允许SVG在缩放时超出容器 */
           width: auto; /* 保持原始宽高比 */
           height: auto; /* 保持原始宽高比 */
           display: block; /* 去除可能的底部空隙 */
        }

        /* ==========================================================================
         * Mermaid样式优化 (浅色模式适配)
         * ========================================================================== */
        
        /* 1. 确保文字清晰可见（根据主题切换） */
        .mermaid-svg-wrapper :global(svg) :global(.nodeLabel),
        .mermaid-svg-wrapper :global(svg) :global(.label),
        .mermaid-svg-wrapper :global(svg) :global(text),
        .mermaid-svg-wrapper :global(svg) :global(.node foreignObject div),
        .mermaid-svg-wrapper :global(svg) :global(.node foreignObject span),
        .mermaid-svg-wrapper :global(svg) :global(.node foreignObject p),
        .mermaid-svg-wrapper :global(svg) :global(g.label foreignObject div) {
           font-weight: 500 !important;
           color: ${isDarkTheme ? '#e5e7eb' : '#333333'} !important;
           fill: ${isDarkTheme ? '#e5e7eb' : '#333333'} !important;
           stroke: none !important;
           text-shadow: none !important; /* 移除阴影 */
           opacity: 1 !important;
        }

        /* 2. 确保默认节点背景与主题匹配 (如果Mermaid没正确应用主题) */
        .mermaid-svg-wrapper :global(.node rect:not([style*="fill"])),
        .mermaid-svg-wrapper :global(.node circle:not([style*="fill"])),
        .mermaid-svg-wrapper :global(.node polygon:not([style*="fill"])),
        .mermaid-svg-wrapper :global(.node path:not([style*="fill"])) {
           fill: ${isDarkTheme ? '#2b2f36' : '#f0f8ff'} !important;
           stroke: ${isDarkTheme ? '#4b5563' : '#cce0ff'} !important;
        }

        /* 3. 连线颜色优化 */
        .mermaid-svg-wrapper :global(.edgePath path),
        .mermaid-svg-wrapper :global(.flowchart-link) {
          stroke: ${isDarkTheme ? '#9ca3af' : '#999999'} !important;
          stroke-opacity: 0.8 !important;
        }

        /* 4. 连线标签背景 */
        .mermaid-svg-wrapper :global(.edgeLabel) {
           background-color: ${isDarkTheme ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.8)'} !important;
           color: ${isDarkTheme ? '#e5e7eb' : '#333333'} !important;
        }
        
        /* 5. 修复集群（子图）背景 */
        .mermaid-svg-wrapper :global(.cluster rect) {
          fill: ${isDarkTheme ? 'rgba(31, 41, 55, 0.6)' : 'rgba(250, 250, 250, 0.5)'} !important;
          stroke: ${isDarkTheme ? '#374151' : '#e0e0e0'} !important;
        }

        /* 添加操作指引 */
        .modal-instructions {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background-color: rgba(0, 0, 0, 0.7);
          color: #ffffff;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          opacity: 0.7;
          transition: opacity 0.3s;
        }
        
        .modal-instructions:hover {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default EnhancedMermaid; 